import { Component, inject, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '../../../../shared/material/material.module';
import { FormBuilder, Validators, FormsModule } from '@angular/forms';
import { AccessPlan, Product, ProductPackage } from '../../../../core/generated';

export interface PlanFullDetailsDialogData {
  plan: any;
  planTotal: number;
  products: Product[];
  productPackages: ProductPackage[];
  onAddItem: (planId: number, itemData: any) => Promise<void>;
  onRemoveItem: (planId: number, itemId: number) => Promise<void>;
  onAddDiscount: (planId: number, discountData: any) => Promise<void>;
  onRemoveDiscount: (planId: number, discountId: number) => Promise<void>;
  onApplyPackage: (planId: number) => Promise<void>;
  onViewPayments: (planId: number) => void;
  onGeneratePayment: (planId: number) => Promise<void>;
  onNavigateToInvoices: (planId: number) => void;
}

@Component({
  selector: 'app-plan-full-details-dialog',
  standalone: true,
  imports: [MaterialModule, FormsModule],
  templateUrl: './plan-full-details-dialog.component.html',
  styleUrl: './plan-full-details-dialog.component.scss'
})
export class PlanFullDetailsDialogComponent {
  private dialogRef = inject(MatDialogRef<PlanFullDetailsDialogComponent>);
  private fb = inject(FormBuilder);

  itemForm = this.fb.group({
    productId: [null as number | null, Validators.required],
    quantity: [1, [Validators.required, Validators.min(1)]],
    unitPrice: [0, [Validators.required, Validators.min(0)]],
  });

  discountForm = this.fb.group({
    type: ['PERCENTAGE' as 'PERCENTAGE' | 'FIXED_AMOUNT', Validators.required],
    description: ['', Validators.required],
    percentage: [0],
    amount: [0],
  });

  applyPackageForm = this.fb.group({
    productPackageId: [null as number | null, Validators.required],
  });

  constructor(@Inject(MAT_DIALOG_DATA) public data: PlanFullDetailsDialogData) {}

  onClose(): void {
    this.dialogRef.close();
  }

  formatDate(date?: string): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('pt-BR');
  }

  formatPrice(price?: number): string {
    if (price === undefined || price === null) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(price);
  }

  formatCpfCnpj(cpfCnpj?: string): string {
    if (!cpfCnpj) return 'N/A';
    const numbers = cpfCnpj.replace(/\D/g, '');
    if (numbers.length === 11) {
      return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    if (numbers.length === 14) {
      return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return cpfCnpj;
  }

  getItemsCount(): number {
    return this.data.plan.items?.length || 0;
  }

  getDiscountsCount(): number {
    return this.data.plan.discounts?.length || 0;
  }

  getDiscountTypeLabel(type?: string): string {
    const typeMap: { [key: string]: string } = {
      'PERCENTAGE': 'Porcentagem',
      'FIXED_AMOUNT': 'Valor Fixo',
      'FIXED': 'Valor Fixo'
    };
    return type ? typeMap[type] || type : 'N/A';
  }

  getDiscountPercentage(discount: any): number | null {
    return discount && discount.percentage !== undefined ? discount.percentage : null;
  }

  getDiscountAmount(discount: any): number | null {
    return discount && discount.amount !== undefined ? discount.amount : null;
  }

  async addItem() {
    if (this.itemForm.valid && this.data.plan.id) {
      await this.data.onAddItem(this.data.plan.id, this.itemForm.value);
      this.itemForm.reset({ productId: null, quantity: 1, unitPrice: 0 });
    }
  }

  async removeItem(itemId: number) {
    if (this.data.plan.id) {
      await this.data.onRemoveItem(this.data.plan.id, itemId);
    }
  }

  async addDiscount() {
    if (this.discountForm.valid && this.data.plan.id) {
      await this.data.onAddDiscount(this.data.plan.id, this.discountForm.value);
      this.discountForm.reset({ type: 'PERCENTAGE', description: '', percentage: 0, amount: 0 });
    }
  }

  async removeDiscount(discountId: number) {
    if (this.data.plan.id) {
      await this.data.onRemoveDiscount(this.data.plan.id, discountId);
    }
  }

  async applyPackage() {
    if (this.applyPackageForm.valid && this.data.plan.id) {
      await this.data.onApplyPackage(this.data.plan.id);
      this.applyPackageForm.reset({ productPackageId: null });
    }
  }

  viewPayments() {
    if (this.data.plan.id) {
      this.data.onViewPayments(this.data.plan.id);
    }
  }

  async generatePayment() {
    if (this.data.plan.id) {
      await this.data.onGeneratePayment(this.data.plan.id);
    }
  }

  navigateToInvoices() {
    if (this.data.plan.id) {
      this.data.onNavigateToInvoices(this.data.plan.id);
    }
  }
}
